import React, { useEffect, useState } from "react";
import { formatGeneratedDate } from "../utils/GeneratedDate";
import axios from "axios";
import { convertToMMYYDDDD } from "../utils/DateUtils";
import { Button } from "../components/UI";
import ImageInspectorModal from "../components/ImageInspectorModal";
import { apiHeaders } from "../hooks";
import { useNavigate } from "../router";
import { getIdVerificationMatchMessage, resolveIdVerificationDisplayState } from "../utils/IdVerificationDisplay";
import {
  SCREENING_STATUS,
  getScreeningStatusDisplay,
  resolveOverallScreeningStatus,
} from "../utils/BackgroundScreening";

function StatusText({ value }) {
  const display = getScreeningStatusDisplay(value);
  return (
    <span
      className={`screening-status screening-status-${display.tone}`}
    >
      <i
        className={`pi ${display.icon}`}
        aria-hidden="true"
      />
      {display.status}
    </span>
  );
}

function ReportSection({
  title,
  icon,
  tone = "blue",
  action,
  children,
  className = "",
}) {
  return (
    <section className={`report-section ${className}`}>
      <div className="report-section-header">
        <span className={`report-section-icon report-section-icon-${tone}`}>
          <i className={`pi ${icon}`} aria-hidden="true" />
        </span>
        <h2>{title}</h2>
        {action ? <div className="report-section-action">{action}</div> : null}
      </div>
      <div className="report-section-body">{children}</div>
    </section>
  );
}

function ReportField({ label, value, mono = false }) {
  return (
    <div className="report-field">
      <span>{label}</span>
      <strong className={mono ? "report-mono" : ""}>{value || "N/A"}</strong>
    </div>
  );
}

function MatchPill({ passed }) {
  return (
    <span
      className={`id-match-pill ${passed ? "id-match-pass" : "id-match-fail"}`}
    >
      <i
        className={`pi ${passed ? "pi-check-circle" : "pi-times-circle"}`}
        aria-hidden="true"
      />
      {passed ? "PASS" : "FAIL"}
    </span>
  );
}

export default function CheckScoreResults({
  guest,
  booking,
  scoreResult,
  generatedAt,
  refreshResults,
}) {
  const [scoreResultLocal, setScoreResultLocal] = useState(scoreResult || {});
  const [idImage, setIdImage] = useState("");
  const [idVerificationState, setIdVerificationState] = useState("loading");
  const [imgUrl, setImgUrl] = useState("");
  const [idImageKey, setIdImageKey] = useState("");
  const [idDocumentDeletedAt, setIdDocumentDeletedAt] = useState("");
  const [idInspectorOpen, setIdInspectorOpen] = useState(false);
  const [nameIsValid, setNameIsValid] = useState(true);
  const [birthDateIsValid, setBirthDateIsValid] = useState(true);
  const [selfieVerificationRequested, setSelfieVerificationRequested] = useState(false);
  const [selfieVerification, setSelfieVerification] = useState({});
  const [selfieInspectorOpen, setSelfieInspectorOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setScoreResultLocal(scoreResult || {});
  }, [scoreResult]);

  useEffect(() => {
    async function getIdVerification() {
      setIdVerificationState("loading");
      setIdImage("");
      setImgUrl("");
      setIdImageKey("");
      setIdDocumentDeletedAt("");
      setIdInspectorOpen(false);
      setSelfieInspectorOpen(false);
      setSelfieVerificationRequested(false);
      setSelfieVerification({});
      if (!scoreResultLocal?.trustdReference) {
        setIdVerificationState("unavailable");
        return;
      }
      try {
        const response = await axios.get(
          `/v1/integrations/stripe/requests/id/${scoreResultLocal.trustdReference}`,
          { headers: apiHeaders },
        );
        const verification = response.data?.idVerification || {};
        const document = verification?.document || {};
        const storage = document?.storage || {};
        const checks = verification?.checks || {};
        const deletedAt =
          document.deletedAt || verification.documentDeletedAt || "";
        const signedUrl = document.signedUrl || response.data.signedUrl || "";
        const fallbackKey =
          response.data?.imageKey ||
          storage?.key ||
          document?.key ||
          verification?.key ||
          "";
        const displayState = resolveIdVerificationDisplayState(response.data);
        setSelfieVerificationRequested(Boolean(response.data?.selfieVerificationRequested));
        setSelfieVerification(response.data?.selfieVerification || {});
        setIdVerificationState(displayState);
        setIdDocumentDeletedAt(deletedAt);
        if (response.data.trustd_reference && response.data.idVerificationRequested) {
          setImgUrl(response.data.image || signedUrl || "");
          setIdImageKey(fallbackKey || "");
          if (signedUrl) setIdImage(signedUrl);
          setNameIsValid(
            checks.firstName !== undefined || checks.lastName !== undefined
              ? Boolean(checks.firstName) && Boolean(checks.lastName)
              : Boolean(response.data.nameIsValid),
          );
          setBirthDateIsValid(
            checks.birthDate !== undefined
              ? Boolean(checks.birthDate)
              : Boolean(response.data.birthDateIsValid),
          );
        }
      } catch (error) {
        console.error("getIdVerification failed:", error);
        setIdVerificationState("unavailable");
      }
    }
    getIdVerification();
  }, [scoreResultLocal?.trustdReference]);

  const getIdImage = async () => {
    if (imgUrl.startsWith("http") && imgUrl.includes("X-Amz-Signature")) {
      setIdImage(imgUrl);
      return;
    }

    let key = idImageKey;
    if (!key && imgUrl && !imgUrl.startsWith("http")) {
      key = imgUrl.replace(/^\/+/, "");
    }
    if (!key && imgUrl.startsWith("http")) {
      try {
        const parsed = new URL(imgUrl);
        key = decodeURIComponent(parsed.pathname.replace(/^\/+/, ""));
      } catch {
        key = "";
      }
    }
    if (!key) return;

    const response = await axios.post(
      "/v1/integrations/stripe/generate-presigned-url",
      { key },
      { headers: apiHeaders },
    );
    if (response.data.status === "success") {
      setIdImage(response.data.url);
      setImgUrl(response.data.url);
    }
  };

  const refreshResultsLocal = async () => {
    if (!refreshResults) return;
    const next = await refreshResults();
    if (next) setScoreResultLocal(next);
  };

  const formatDate = (value) => convertToMMYYDDDD(value) || "N/A";
  const fullName =
    [guest?.firstName, guest?.middleName, guest?.lastName]
      .filter(Boolean)
      .join(" ") || "Guest";
  const stayDates = booking
    ? `${formatDate(booking.checkInDate)} - ${formatDate(booking.checkOutDate)}`
    : "Reservation dates unavailable";
  const listingName = booking?.listingName || "Listing unavailable";
  const reportId = scoreResultLocal.trustdReference || "N/A";
  const idPassed = Boolean(nameIsValid && birthDateIsValid);
  const idVerificationHasResult =
    idVerificationState === "verified" || idVerificationState === "failed";
  const idVerificationMessage = {
    not_requested: {
      title: "ID Verification was not requested for this guest.",
      detail:
        "Enable ID Verification in Account Settings if you require guests to upload their IDs.",
    },
    pending: {
      title: "ID Verification is still in progress.",
      detail:
        "The guest submitted an ID. The verification result will appear here when processing is complete.",
    },
    error: {
      title: "ID Verification could not be completed.",
      detail:
        "The guest submitted an ID, but verification processing failed. Please contact support or retry the verification.",
    },
    unavailable: {
      title: "ID Verification information could not be loaded.",
      detail:
        "This does not mean verification was not requested. Refresh the report or try again shortly.",
    },
  }[idVerificationState];
  const selfiePassed = selfieVerification?.selfieVerificationPass === true;
  const selfieHasResult = ["matched", "no_match"].includes(selfieVerification?.status);
  const selfieImage = selfieVerification?.document?.signedUrl || "";
  const screeningItems = [
    {
      label: "Criminal Records",
      value: scoreResultLocal.criminalList,
      description: scoreResultLocal.criminalListDescription,
    },
    {
      label: "Sex Offender Registry",
      value: scoreResultLocal.sexOffenderList,
      description: scoreResultLocal.sexOffenderListDescription,
    },
    {
      label: "OFAC Sanctions",
      value: scoreResultLocal.ofacList,
      description: scoreResultLocal.ofacListDescription,
    },
    {
      label: "UN Sanctions",
      value: scoreResultLocal.unList,
      description: scoreResultLocal.unListDescription,
    },
    {
      label: "FBI Wanted (Public API)",
      value: scoreResultLocal.fbiList,
      description: scoreResultLocal.fbiListDescription,
    },
  ];
  const overallScreeningStatus = resolveOverallScreeningStatus(scoreResultLocal);
  const hasPotentialMatch =
    overallScreeningStatus === SCREENING_STATUS.POTENTIAL_MATCH;
  const hasSoftMatch = overallScreeningStatus === SCREENING_STATUS.SOFT_MATCH;
  const hasRiskMatch = hasPotentialMatch || hasSoftMatch;
  const hasUnavailableSource = screeningItems.some(
    (item) => String(item.value || "").toUpperCase() === "UNAVAILABLE",
  );

  return (
    <>
      <div className="checkScoreResult checkscore-results screening-report">
        <header className="screening-report-hero">
          <div>
            <button
              className="report-back-button"
              type="button"
              onClick={() => navigate("/reviews")}
            >
              <i className="pi pi-arrow-left" aria-hidden="true" />
              Back to My Guests
            </button>
            <p>Guest Screening Report</p>
            <h1>{fullName}</h1>
            <span>
              {stayDates} | {listingName}
            </span>
          </div>
          <div className="report-verified-block">
            <span className="report-reference report-mono">ID: {reportId}</span>
            <span className="report-generated-at">
              Generated: {formatGeneratedDate(generatedAt)}
            </span>
          </div>
        </header>

        {guest && booking ? (
          <ReportSection title="Reservation Information" icon="pi-calendar">
            <div className="report-info-grid">
              <ReportField label="First Name" value={guest.firstName} />
              <ReportField label="Middle Name" value={guest.middleName} />
              <ReportField label="Last Name" value={guest.lastName} />
              <ReportField
                label="Date of Birth"
                value={formatDate(guest.birthDate)}
              />
              <ReportField label="Email" value={guest.email} />
              <ReportField label="Phone" value={guest.phone} />
              <ReportField
                label="Check-In Date"
                value={formatDate(booking.checkInDate)}
              />
              <ReportField
                label="Check-Out Date"
                value={formatDate(booking.checkOutDate)}
              />
              <ReportField label="Listing" value={booking.listingName} />
              <ReportField
                label="Channel"
                value={booking.channel || booking.channelName}
              />
            </div>
          </ReportSection>
        ) : null}

        {idVerificationState !== "loading" ? (
          idVerificationHasResult ? (
            <ReportSection
              title="Guest ID Verification"
              icon="pi-id-card"
              action={<MatchPill passed={idPassed} />}
            >
              <div className="report-id-layout">
                <div className="report-id-media">
                  {idDocumentDeletedAt ? (
                    <div className="id-document-deleted-notice" role="status">
                      <i className="pi pi-shield" aria-hidden="true" />
                      <strong>ID photo deleted</strong>
                      <span>
                        For security, this guest ID photo was permanently
                        deleted after the checkout date passed.
                      </span>
                    </div>
                  ) : idImage ? (
                    <img
                      alt="Guest ID"
                      src={idImage}
                      className="id-image id-image-clickable"
                      onClick={() => setIdInspectorOpen(true)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          setIdInspectorOpen(true);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      referrerPolicy="no-referrer"
                      draggable={false}
                    />
                  ) : (
                    <button
                      className="noImgBox checkscore-id-placeholder"
                      type="button"
                      onClick={getIdImage}
                    >
                      <i className="pi pi-image" aria-hidden="true" />
                      <span>
                        Click to view
                        <br />
                        Guest ID
                      </span>
                    </button>
                  )}
                </div>
                <div className="report-id-content">
                  <div className="report-id-checks">
                    <div className="report-id-check-card">
                      <span>Full Name on ID</span>
                      <div>
                        <strong>{fullName}</strong>
                        <MatchPill passed={nameIsValid} />
                      </div>
                    </div>
                    <div className="report-id-check-card">
                      <span>Date of Birth on ID</span>
                      <div>
                        <strong>{formatDate(guest?.birthDate)}</strong>
                        <MatchPill passed={birthDateIsValid} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={`report-id-note ${idPassed ? "report-id-note-pass" : "report-id-note-fail"}`}
              >
                <i
                  className={`pi ${idPassed ? "pi-check-circle" : "pi-times-circle"}`}
                  aria-hidden="true"
                />
                <p>{getIdVerificationMatchMessage(nameIsValid, birthDateIsValid)}</p>
              </div>
              <div className="report-guest-privacy">
                <i className="pi pi-shield" aria-hidden="true" />
                <p><strong>Guest privacy protected:</strong> Guest ID will be deleted after the check-out date.</p>
              </div>
            </ReportSection>
          ) : (
            <ReportSection title="Guest ID Verification" icon="pi-id-card">
              <div className="checkscore-id-off-card">
                <span className="checkscore-id-off-badge">ID Verification</span>
                <h4>{idVerificationMessage?.title}</h4>
                <p>{idVerificationMessage?.detail}</p>
              </div>
              {idVerificationState !== "not_requested" ? (
                <div className="report-guest-privacy">
                  <i className="pi pi-shield" aria-hidden="true" />
                  <p><strong>Guest privacy protected:</strong> Guest ID will be deleted after the check-out date.</p>
                </div>
              ) : null}
            </ReportSection>
          )
        ) : null}

        {idVerificationState !== "loading" ? (
          selfieVerificationRequested ? (
            <ReportSection
              title="Selfie Verification"
              icon="pi-user"
              tone={selfieHasResult ? (selfiePassed ? "green" : "red") : "amber"}
              action={selfieHasResult ? <MatchPill passed={selfiePassed} /> : <span className="report-new-badge">Pending</span>}
              className="selfie-section"
            >
              {selfieHasResult ? (
                <div className="selfie-result-layout">
                  <div className="selfie-result-photo">
                    {selfieImage ? (
                      <button type="button" onClick={() => setSelfieInspectorOpen(true)} aria-label="Open guest selfie">
                        <img src={selfieImage} alt="Guest selfie" referrerPolicy="no-referrer" />
                      </button>
                    ) : (
                      <div><i className="pi pi-user" /><span>Selfie unavailable</span></div>
                    )}
                  </div>
                  <div className="selfie-result-content">
                    <div className={`selfie-match-summary ${selfiePassed ? "selfie-match-summary-pass" : "selfie-match-summary-fail"}`}>
                      <span className="selfie-match-summary-icon">
                        <i className={`pi ${selfiePassed ? "pi-verified" : "pi-exclamation-triangle"}`} aria-hidden="true" />
                      </span>
                      <div>
                        <strong>{selfiePassed ? "Face Match Successful" : "Face Match Unavailable"}</strong>
                        <p>{selfiePassed
                          ? "The submitted selfie was successfully matched to the picture on the uploaded ID."
                          : "The submitted selfie could not be confidently matched to the picture on the uploaded ID."}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="checkscore-id-off-card">
                  <span className="checkscore-id-off-badge">Selfie Verification</span>
                  <h4>Selfie comparison is still in progress.</h4>
                  <p>The result will appear here after the guest's selfie is securely compared with their ID.</p>
                </div>
              )}
              <div className="report-guest-privacy">
                <i className="pi pi-shield" aria-hidden="true" />
                <p><strong>Guest privacy protected:</strong> Guest selfie will be deleted once the verification process is complete.</p>
              </div>
            </ReportSection>
          ) : (
            <ReportSection title="Selfie Verification" icon="pi-user">
              <div className="checkscore-id-off-card">
                <span className="checkscore-id-off-badge">Selfie Verification</span>
                <h4>{idVerificationState === "unavailable"
                  ? "Selfie Verification information could not be loaded."
                  : "Selfie Verification was not requested for this guest."}</h4>
                <p>{idVerificationState === "unavailable"
                  ? "Refresh the report or try again shortly."
                  : "Enable Selfie Verification in Account Settings if you require guests to submit a selfie."}</p>
              </div>
            </ReportSection>
          )
        ) : null}

        <ReportSection
          title="Background Screening"
          icon="pi-shield"
          tone={hasRiskMatch ? "red" : hasUnavailableSource ? "amber" : "green"}
        >
          <div className="report-screening-grid">
            {screeningItems.map((item) => (
              <div
                key={item.label}
                className="report-screening-card report-screening-card-static"
              >
                <span>{item.label}</span>
                <StatusText value={item.value} />
                {item.description ? (
                  <small className="screening-status-description">
                    {item.description}
                  </small>
                ) : null}
              </div>
            ))}
            <div className="report-screening-card report-screening-card-static">
              <span>Booking IP Record</span>
              <strong>{scoreResultLocal.isSuspiciousIp || "N/A"}</strong>
            </div>
          </div>
          {hasPotentialMatch ? (
            <p className="report-screening-action-note report-screening-action-note-potential">
              <strong>Action on your part:</strong> Make a decision at your
              discretion. If you choose to cancel, contact us via Contact Us to
              request a Guest-Facing Report to share with the guest.
            </p>
          ) : hasSoftMatch ? (
            <p className="report-screening-action-note report-screening-action-note-soft">
              <strong>Action on your part:</strong> You may continue hosting at
              your own discretion. If you choose to cancel, contact us via
              Contact Us to request a Guest-Facing Report.
            </p>
          ) : null}
          <div className="report-id-strip">
            <span>Unique Report ID</span>
            <strong className="report-mono">{reportId}</strong>
          </div>
        </ReportSection>

        {scoreResultLocal.linkedinSearch ? (
          <ReportSection
            title="Social Media Presence"
            icon="pi-linkedin"
            action={
              scoreResultLocal.linkedinSearch.status === "Pending" ? (
                <Button label="Refresh" onClick={refreshResultsLocal} />
              ) : null
            }
          >
            <div className="report-info-grid report-info-grid-two">
              <ReportField
                label="Status"
                value={scoreResultLocal.linkedinSearch.status}
              />
              <ReportField
                label="Match Count"
                value={scoreResultLocal.linkedinSearch.matchCount}
              />
            </div>
          </ReportSection>
        ) : null}

        {scoreResultLocal.phoneLookup ? (
          <ReportSection title="Phone Lookup" icon="pi-phone">
            <div className="report-info-grid report-info-grid-two">
              <ReportField
                label="Name"
                value={`${scoreResultLocal.phoneLookup.firstName || ""} ${scoreResultLocal.phoneLookup.middleName || ""} ${scoreResultLocal.phoneLookup.lastName || ""}`.trim()}
              />
              <ReportField
                label="Location"
                value={`${scoreResultLocal.phoneLookup.city || ""} ${scoreResultLocal.phoneLookup.state || ""} ${scoreResultLocal.phoneLookup.zip || ""}`.trim()}
              />
              <ReportField
                label="Carrier"
                value={scoreResultLocal.phoneLookup.carrier}
              />
              <ReportField
                label="Type"
                value={scoreResultLocal.phoneLookup.phoneType}
              />
            </div>
          </ReportSection>
        ) : null}

        <section className="report-disclaimer">
          <h2>LEGAL DISCLAIMER &amp; LIMITATION OF LIABILITY</h2>
          <ol>
            <li>
              <strong>As-Is Data Provided:</strong> Trustd.ai utilizes automated
              processes to aggregate publicly available data. This information
              is provided on an "AS IS" and "AS AVAILABLE" basis. Trustd.ai does
              not verify, validate, or audit the accuracy, completeness, or
              reliability of any third-party data.
            </li>
            <li>
              <strong>No Liability:</strong> Trustd.ai expressly disclaims any
              responsibility for errors, omissions, or inaccuracies in the data.
              Trustd.ai, its affiliates, and its employees shall not be liable
              for any direct, indirect, incidental, or consequential damages,
              including financial loss or reputational harm, arising from
              decisions or actions taken based on the information provided
              through this platform.
            </li>
            <li>
              <strong>Host Responsibility &amp; Action:</strong> Trustd.ai is a
              data tool and does not make reservation or safety decisions. The
              Host is the sole decision-maker.
              <div className="report-disclaimer-subitems">
                <p>
                  <strong>Notice Requirement:</strong> If a Host takes any
                  negative action, such as cancelling a reservation, based on
                  data found through Trustd.ai, the Host is solely responsible
                  for notifying the guest of that decision.
                </p>
                <p>
                  <strong>Guest-Facing Reports:</strong> To ensure transparency
                  and accuracy, Hosts are prohibited from sharing raw Trustd.ai
                  internal data with guests. If a notification is required, the
                  Host must contact Trustd.ai to request an official{" "}
                  <strong>"Guest-Facing Report"</strong> to provide to the
                  guest.
                </p>
              </div>
            </li>
            <li>
              <strong>Indemnification:</strong> By using this service, the Host
              agrees to indemnify, defend, and hold harmless Trustd.ai from any
              and all claims, legal fees, or damages resulting from the Host's
              use of the data, failure to provide proper notice to guests, or
              any misinterpretation of the records provided.
            </li>
          </ol>
        </section>
      </div>

      <ImageInspectorModal
        visible={
          idInspectorOpen &&
          Boolean(idImage) &&
          !Boolean(idDocumentDeletedAt)
        }
        src={idImage}
        alt="Guest ID"
        title="Guest ID Fullscreen Inspector"
        onClose={() => setIdInspectorOpen(false)}
      />
      <ImageInspectorModal
        visible={selfieInspectorOpen && Boolean(selfieImage)}
        src={selfieImage}
        alt="Guest selfie"
        title="Guest Selfie Fullscreen Inspector"
        onClose={() => setSelfieInspectorOpen(false)}
      />
    </>
  );
}
